import { DBSchema, IDBPDatabase, openDB } from 'idb';

/**
 * Single IndexedDB database for all SW state. Later phases add stores by
 * bumping SW_DB_VERSION and extending the upgrade switch — entity caches are
 * disposable (bump + refetch), event/upload queues are NOT and must be
 * preserved across upgrades.
 */

export interface QueuedUpload {
  /** MD5 checksum — content address and queue key (server dedupes on it). */
  checksum: string;
  /** Request path, e.g. /api/attachments/<checksum> */
  url: string;
  contentType: string;
  filename: string | null;
  /** Request body. Stored as Blob — Request objects are not structured-cloneable. */
  blob: Blob;
  createdAt: string;
}

export interface QueuedEvent {
  /** Client-generated event uuid — queue key; the server dedupes on it. */
  id: string;
  /** Event POST resource. */
  resource: 'ticketevents' | 'listevents';
  /** The event DTO exactly as the window POSTed it. */
  event: Record<string, unknown>;
  /** Event creation date — determines POST order. */
  created: string;
  queuedAt: string;
}

export interface MetaEntry {
  key: string;
  value: unknown;
}

export interface StoredEntity {
  /** `${resource}/${id}` */
  key: string;
  resource: string;
  id: string;
  data: Record<string, unknown>;
  updatedAt: string;
}

export interface SwDBSchema extends DBSchema {
  uploadQueue: {
    key: string;
    value: QueuedUpload;
  };
  entities: {
    key: string;
    value: StoredEntity;
    indexes: { 'by-resource': string };
  };
  eventQueue: {
    key: string;
    value: QueuedEvent;
    indexes: { 'by-resource': string };
  };
  meta: {
    key: string;
    value: MetaEntry;
  };
}

export type SwDB = IDBPDatabase<SwDBSchema>;

export const SW_DB_NAME = 'certhon-sw';
export const SW_DB_VERSION = 3;

export function openSwDb(name: string = SW_DB_NAME): Promise<SwDB> {
  return openDB<SwDBSchema>(name, SW_DB_VERSION, {
    upgrade(db, oldVersion) {
      if (oldVersion < 1) {
        db.createObjectStore('uploadQueue', { keyPath: 'checksum' });
      }
      if (oldVersion < 2) {
        const entities = db.createObjectStore('entities', { keyPath: 'key' });
        entities.createIndex('by-resource', 'resource');
      }
      if (oldVersion < 3) {
        const events = db.createObjectStore('eventQueue', { keyPath: 'id' });
        events.createIndex('by-resource', 'resource');
        db.createObjectStore('meta', { keyPath: 'key' });
      }
    },
  });
}
